import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

// Hook to integrate Segment analytics. Call this in the main App component to initialize.
// Call inside other components to get access to trackEvent function.
// The hook will only load Segment if VITE_SEGMENT_WRITE_KEY is set, and it will only load once.
export const useSegment = () => {
  const segmentWriteKey = import.meta.env.VITE_SEGMENT_WRITE_KEY;
  const analyticsRef = useRef<any>(null);

  // Insert Segment analytics script on mount
  useEffect(() => {
    if (!segmentWriteKey) {
      return;
    }

    // define the key where the global analytics object will be accessible
    // customers can safely set this to be something else if need be
    const globalAnalyticsKey: string = "analytics";

    // Create a queue, but don't obliterate an existing one!
    const analytics: any = ((window as any)[globalAnalyticsKey] =
      (window as any)[globalAnalyticsKey] || []);

    // If the real analytics.js is already on the page, return.
    if (analytics.initialize) return;

    // If the snippet was invoked already show an error.
    if (analytics.invoked) {
      if (window.console && console.info) {
        console.info("Segment snippet included twice.");
      }
      return;
    }

    // Invoked flag, to make sure the snippet
    // is never invoked twice.
    analytics.invoked = true;

    // A list of the methods in Analytics.js to stub.
    analytics.methods = [
      "trackSubmit",
      "trackClick",
      "trackLink",
      "trackForm",
      "pageview",
      "identify",
      "reset",
      "group",
      "track",
      "ready",
      "alias",
      "debug",
      "page",
      "screen",
      "once",
      "off",
      "on",
      "addSourceMiddleware",
      "addIntegrationMiddleware",
      "setAnonymousId",
      "addDestinationMiddleware",
      "register",
    ];

    // Define a factory to create stubs. These are placeholders
    // for methods in Analytics.js so that you never have to wait
    // for it to load to actually record data. The `method` is
    // stored as the first argument, so we can replay the data.
    analytics.factory = function (e: string) {
      return function () {
        if ((window as any)[globalAnalyticsKey].initialized) {
          // Sometimes users assigned analytics to a variable before analytics is done loading, resulting in a stale reference.
          // If so, proxy any calls to the 'real' analytics instance.
          // eslint-disable-next-line prefer-spread
          return (window as any)[globalAnalyticsKey][e].apply(
            (window as any)[globalAnalyticsKey],
            // eslint-disable-next-line prefer-rest-params
            arguments
          );
        }
        // eslint-disable-next-line prefer-rest-params
        const args = Array.prototype.slice.call(arguments);

        // Add buffered page context object so page information is always up-to-date
        if (["track", "screen", "alias", "group", "page", "identify"].indexOf(e) > -1) {
          const c = document.querySelector("link[rel='canonical']");
          args.push({
            __t: "bpc",
            c: (c && c.getAttribute("href")) || undefined,
            p: location.pathname,
            u: location.href,
            s: location.search,
            t: document.title,
            r: document.referrer,
          });
        }

        args.unshift(e);
        analytics.push(args);
        return analytics;
      };
    };

    // For each of our methods, generate a queueing stub.
    for (let i = 0; i < analytics.methods.length; i++) {
      const key = analytics.methods[i];
      analytics[key] = analytics.factory(key);
    }

    // Define a method to load Analytics.js from our CDN,
    // and that will be sure to only ever load it once.
    analytics.load = function (key: string, options: any) {
      // Create an async script element based on your key.
      const t = document.createElement("script");
      t.type = "text/javascript";
      t.async = true;
      t.setAttribute("data-global-segment-analytics-key", globalAnalyticsKey);
      t.src = "https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";

      // Insert our script next to the first script element.
      const first = document.getElementsByTagName("script")[0];
      if (first.parentNode) {
        first.parentNode.insertBefore(t, first);
      }
      analytics._loadOptions = options;
    };
    analytics._writeKey = segmentWriteKey;

    // Add a version to keep track of what's in the wild.
    analytics.SNIPPET_VERSION = "5.2.1";

    // Load Analytics.js with your key, which will automatically
    // load the tools you've enabled for your account. Boosh!
    analytics.load(segmentWriteKey);

    analyticsRef.current = analytics;
  }, [segmentWriteKey]);

  return analyticsRef.current;
};

export const useSegmentPage = () => {
  const segment = useSegment();
  const location = useLocation();

  useEffect(() => {
    if (segment && typeof segment.page === "function") {
      segment.page();
    }
  }, [location.pathname, segment]);
};

export const useSegmentTrackEvent = () => {
  const segment = useSegment();

  const trackEvent = (eventName: string, payload: any) => {
    if (segment && typeof segment.track === "function") {
      segment.track(eventName, payload);
    }
  };

  return trackEvent;
};
